import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { fetchNewsComments, addNewsComment, deleteNewsComment } from '../services/newsArticleService';
import type { NewsComment } from '../types/newsArticle';

const MAX_LENGTH = 1000;

interface Props {
  articleId: string;
}

const NewsCommentSection: React.FC<Props> = ({ articleId }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState<NewsComment[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, total: t } = await fetchNewsComments(articleId);
    setComments(data);
    setTotal(t);
    setLoading(false);
  }, [articleId]);

  useEffect(() => { load(); }, [load]);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('nb-NO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !text.trim()) return;
    setSubmitting(true);
    setSubmitError('');

    const { data: newComment, error } = await addNewsComment(articleId, user.id, text.trim());
    if (error || !newComment) {
      setSubmitError(error?.message ?? 'Kunne ikke sende kommentar.');
      setSubmitting(false);
      return;
    }

    setText('');
    setComments((prev) => [...prev, newComment]);
    setTotal((prev) => prev + 1);
    setSubmitting(false);
  };

  const handleDelete = async (commentId: string) => {
    setDeletingId(commentId);
    const { error } = await deleteNewsComment(commentId);
    if (!error) {
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      setTotal((prev) => prev - 1);
    }
    setDeletingId(null);
  };

  return (
    <section
      id="kommentarer"
      className="mt-12 pt-8"
      style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}
    >
      {/* Header */}
      <h2 className="text-sm font-bold tracking-widest uppercase mb-6" style={{ color: 'rgba(255,255,255,0.35)' }}>
        Kommentarer{total > 0 && <span className="ml-2" style={{ color: 'rgba(255,255,255,0.2)' }}>({total})</span>}
      </h2>

      {/* Comment form */}
      {user ? (
        <form onSubmit={handleSubmit} className="mb-10">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (text.trim() && !submitting) handleSubmit(e as unknown as React.FormEvent);
              }
            }}
            maxLength={MAX_LENGTH}
            rows={3}
            placeholder="Skriv en kommentar… (Enter for å sende)"
            className="w-full resize-y min-h-[90px] text-sm bg-transparent text-white placeholder-white/20 outline-none focus:outline-none py-3 pr-3"
            style={{
              borderBottom: '1px solid rgba(255,255,255,0.15)',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => (e.currentTarget.style.borderBottomColor = 'rgba(255,255,255,0.4)')}
            onBlur={(e) => (e.currentTarget.style.borderBottomColor = 'rgba(255,255,255,0.15)')}
          />
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>
              {text.length}/{MAX_LENGTH}
            </span>
            <button
              type="submit"
              disabled={submitting || !text.trim()}
              className="text-xs font-bold tracking-widest uppercase px-5 py-2 transition-all disabled:opacity-30"
              style={{
                color: 'rgba(255,255,255,0.6)',
                border: '1px solid rgba(255,255,255,0.15)',
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)';
              }}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)')}
            >
              {submitting ? 'Sender…' : 'Send'}
            </button>
          </div>
          {submitError && <p className="mt-2 text-xs text-red-400">{submitError}</p>}
        </form>
      ) : (
        <p className="mb-8 text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>
          <Link to="/login" className="underline hover:text-white transition-colors">Logg inn</Link>{' '}
          for å kommentere.
        </p>
      )}

      {/* Comment list */}
      {loading ? (
        <p className="text-xs animate-pulse" style={{ color: 'rgba(255,255,255,0.25)' }}>
          Laster kommentarer…
        </p>
      ) : comments.length === 0 ? (
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.25)' }}>
          Ingen kommentarer ennå. Bli den første!
        </p>
      ) : (
        <ul className="space-y-7">
          {comments.map((comment) => (
            <li key={comment.id} className="group/c flex gap-4">
              {/* Avatar initial */}
              <div
                className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' }}
              >
                {comment.author_display_name
                  ? comment.author_display_name.charAt(0).toUpperCase()
                  : '?'}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-3 mb-1 flex-wrap">
                  <span className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    {comment.author_display_name ?? 'Ukjent'}
                  </span>
                  <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>
                    {formatDate(comment.created_at)}
                  </span>
                </div>
                <p
                  className="text-sm leading-relaxed whitespace-pre-wrap break-words"
                  style={{ color: 'rgba(255,255,255,0.65)' }}
                >
                  {comment.content}
                </p>
              </div>

              {user?.id === comment.author_id && (
                <button
                  type="button"
                  onClick={() => handleDelete(comment.id)}
                  disabled={deletingId === comment.id}
                  className="shrink-0 self-start pt-1 text-xs opacity-0 group-hover/c:opacity-100 transition-opacity disabled:opacity-30"
                  style={{ color: 'rgba(239,68,68,0.6)' }}
                  aria-label="Slett kommentar"
                >
                  {deletingId === comment.id ? '…' : 'Slett'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default NewsCommentSection;
